import { useEffect, useRef } from "react";

import { isMac } from "~/utils";

export type ShortcutKey = "meta" | "shift" | "alt" | "ctrl" | (string & {});

function isModifierKey(key: string): key is "meta" | "shift" | "alt" | "ctrl" {
  return ["meta", "shift", "alt", "ctrl"].includes(key);
}

function matchesShortcut(event: KeyboardEvent, keys: ShortcutKey[]): boolean {
  const modifiers = new Set(keys.filter(isModifierKey));
  const key = keys.find((k) => !isModifierKey(k)) || "";

  if (event.key.toLowerCase() !== key.toLowerCase()) {
    return false;
  }

  const metaPressed = isMac ? event.metaKey : event.ctrlKey;
  const ctrlPressed = isMac ? event.ctrlKey : event.ctrlKey;

  if (modifiers.has("meta") !== metaPressed) return false;
  if (modifiers.has("ctrl") && !ctrlPressed) return false;
  if (modifiers.has("shift") !== event.shiftKey) return false;
  if (modifiers.has("alt") !== event.altKey) return false;

  return true;
}

export function useKeyboardShortcut(keys: ShortcutKey[], callback: () => void): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  // Synchronize with external system (window keydown events)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (matchesShortcut(event, keys)) {
        event.preventDefault();
        callbackRef.current();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [keys]);
}
